import { api } from './authService';

export interface EmailSender {
  id?: number;
  smtp_host: string;
  smtp_port: number;
  smtp_secure: boolean;
  smtp_user: string;
  smtp_from?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface EmailRecipient {
  id?: number;
  email: string;
  recipient_types: string; // 逗号分隔的类型
  description?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface EmailSendLog {
  id: number;
  sender_id?: number;
  recipient_id?: number;
  recipient_email: string;
  recipient_types?: string;
  subject?: string;
  content?: string;
  status: 'success' | 'failed' | 'pending';
  error_message?: string;
  sent_at?: string;
  created_at: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export const emailConfigService = {
  /**
   * 获取发件人配置
   */
  async getSender(): Promise<ApiResponse<EmailSender>> {
    const response = await api.get<ApiResponse<EmailSender>>('/email-config/sender');
    return response.data;
  },

  /**
   * 保存发件人配置
   */
  async saveSender(config: Omit<EmailSender, 'id' | 'created_at' | 'updated_at'> & { smtp_pass: string }): Promise<ApiResponse<void>> {
    const response = await api.post<ApiResponse<void>>('/email-config/sender', config);
    return response.data;
  },

  /**
   * 测试发件人配置
   */
  async testSender(testEmail: string): Promise<ApiResponse<void>> {
    const response = await api.post<ApiResponse<void>>('/email-config/sender/test', {
      test_email: testEmail
    });
    return response.data;
  },

  /**
   * 获取收件人列表
   */
  async getRecipients(recipientType?: string): Promise<ApiResponse<EmailRecipient[]>> {
    const params = recipientType ? `?recipient_type=${encodeURIComponent(recipientType)}` : '';
    const response = await api.get<ApiResponse<EmailRecipient[]>>(`/email-config/recipients${params}`);
    return response.data;
  },

  /**
   * 添加收件人
   */
  async addRecipient(recipient: Omit<EmailRecipient, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<void>> {
    const response = await api.post<ApiResponse<void>>('/email-config/recipients', recipient);
    return response.data;
  },

  /**
   * 更新收件人
   */
  async updateRecipient(id: number, recipient: Partial<EmailRecipient>): Promise<ApiResponse<void>> {
    const response = await api.put<ApiResponse<void>>(`/email-config/recipients/${id}`, recipient);
    return response.data;
  },

  /**
   * 删除收件人
   */
  async deleteRecipient(id: number): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`/email-config/recipients/${id}`);
    return response.data;
  },

  /**
   * 获取发送记录
   */
  async getSendLogs(query: {
    sender_id?: number;
    recipient_id?: number;
    recipient_type?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<PaginatedResponse<EmailSendLog>> {
    const params = new URLSearchParams();
    if (query.sender_id) params.append('sender_id', query.sender_id.toString());
    if (query.recipient_id) params.append('recipient_id', query.recipient_id.toString());
    if (query.recipient_type) params.append('recipient_type', query.recipient_type);
    if (query.status) params.append('status', query.status);
    if (query.start_date) params.append('start_date', query.start_date);
    if (query.end_date) params.append('end_date', query.end_date);
    if (query.page) params.append('page', query.page.toString());
    if (query.limit) params.append('limit', query.limit.toString());

    const response = await api.get<PaginatedResponse<EmailSendLog>>(
      `/email-config/send-logs?${params.toString()}`
    );
    return response.data;
  }
};

